import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, fecha, hoy } from '../utils/formato';
import { filtrarRecibos } from '../utils/buscar';
import { disponible } from '../utils/leche_disponible';
import { TIPOS, VACA, precioDe, tiposQueVende, valorDeEntrega } from '../utils/leche';
import ElegirFinca from './ElegirFinca';
import { encolar, pendientes as pendientesEnCola, enviarPendientes } from '../utils/cola';

// Recibir la leche del día.
//
// Es lo primero que pasa cada mañana y lo que más veces se hace, así que el
// formulario propone todo: la fecha de hoy y el precio pactado de la finca que
// se elija. Quien recibe solo escribe los litros.
function RecibirLeche() {
  const [fincas, setFincas] = useState([]);
  const [recibos, setRecibos] = useState([]);
  const [fincaId, setFincaId] = useState('');
  const [litros, setLitros] = useState('');
  const [tipoLeche, setTipoLeche] = useState(VACA);
  // Si al terminar se guarda el precio en la ficha. Viene marcado: quien está
  // anotando una leche nueva casi siempre la va a seguir comprando.
  const [pactar, setPactar] = useState(true);
  const [precio, setPrecio] = useState('');
  // Lo escrito en la caja de búsqueda de las entregas.
  const [buscando, setBuscando] = useState('');
  const [dia, setDia] = useState(hoy());
  const [nota, setNota] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);

  // El recibo que se está corrigiendo. Un número mal tecleado que no se puede
  // arreglar es lo que hace que la gente deje de confiar en un programa y se
  // vuelva al cuaderno.
  const [corrigiendo, setCorrigiendo] = useState(null);
  const [centros, setRutas] = useState([]);
  const [centroId, setCentroId] = useState('');
  const [pendientes, setPendientes] = useState([]);
  const [lecheDia, setLecheDia] = useState(null);
  // QUÉ PERIODO SE ESTÁ MIRANDO.
  //
  // Primero fue un día suelto —«que él pueda ver cuántos litros entraron en un
  // día determinado»—. Después pidió el rango: «los registros del 16 de agosto
  // al 20 de agosto».
  //
  // Se hizo UN solo control para las dos cosas en vez de agregarle un
  // «desde/hasta» a la lista dejando el día suelto arriba: tres casillas de
  // fecha en una pantalla es la manera de que alguien filtre por una y lea la
  // cifra de la otra. Con las dos fechas iguales es un día; distintas, un
  // rango. Y gobierna TODO lo de la pantalla — las tarjetas y la lista.
  // Cuántas caben en la lista. Empieza en 60 y se suelta con un botón: pintar
  // quinientos renglones de entrada hace lenta la pantalla en el PC del
  // muchacho, pero esconderlos sin salida hace inútil el rango de fechas —que
  // existe precisamente para mirar varios días a la vez—.
  const [verTodas, setVerTodas] = useState(false);
  const [desde, setDesde] = useState(hoy());
  const [hasta, setHasta] = useState(hoy());
  const unSoloDia = desde === hasta;
  const [mandando, setMandando] = useState(false);

  const cargar = useCallback(() => {
    axios.get('/api/fincas').then(r => setFincas(r.data || [])).catch(() => {});
    // A qué ruta llegó esta leche. Con uno solo se anota sin
    // preguntar; con varios hay que elegir, porque de ahí sale el rendimiento
    // de cada sitio.
    axios.get('/api/centros').then(r => {
      const lista = r.data || [];
      setRutas(lista);
      if (lista.length === 1) setCentroId(String(lista[0].id));
    }).catch(() => {});
    // EL RANGO VA AL SERVIDOR, no se filtra aquí lo que llegue.
    //
    // Esta consulta trae como mucho 500 recibos, de lo más nuevo a lo más
    // viejo. Gabriel ya tiene 605: los 105 más viejos —el 15, el 16 y parte
    // del 17 de agosto— nunca llegaban al navegador. Buscar el 16 daba cero
    // aunque los 70 registros de ese día estuvieran guardados, y nada en la
    // pantalla decía que faltaba nada.
    //
    // Pidiendo el periodo, el servidor manda ESE periodo y el tope deja de
    // estorbar. Y cada día que pase habría empeorado.
    axios.get('/api/recibos-leche', { params: { desde, hasta } })
      .then(r => setRecibos(r.data || [])).catch(() => {});
    // La leche del día: lo que entró, lo que se fue a las tinas y lo que se
    // vendió cruda. Con la MISMA función que usa la pantalla de vender —dos
    // cuentas de la misma leche terminan diciendo cifras distintas.
    // El cuadre es de UN día por naturaleza —lo que entró menos lo que se
    // cuajó y se vendió ese día—, así que se pide el del final del rango.
    axios.get(`/api/leche/dia?fecha=${hasta}`)
      .then(r => setLecheDia(r.data || null)).catch(() => setLecheDia(null));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  // Mandar lo que quedó guardado en el teléfono.
  //
  // Se intenta al abrir la pantalla y cada vez que vuelve la señal. No hay un
  // botón obligatorio: si hiciera falta acordarse de tocarlo, los recibos se
  // quedarían ahí — y el que no se acuerda es justo el que estaba en la finca
  // sin señal.
  const mandarPendientes = useCallback(async () => {
    if (pendientesEnCola().length === 0 || !navigator.onLine) return;
    setMandando(true);
    const r = await enviarPendientes((recibo) =>
      axios.post('/api/recibos-leche', recibo));
    setPendientes(pendientesEnCola());
    setMandando(false);
    if (r.enviados > 0) {
      setAviso({ tipo: 'bueno',
        texto: `Se enviaron ${r.enviados} ${r.enviados === 1 ? 'recibo' : 'recibos'} ` +
               'que estaban guardados en el teléfono.' });
      cargar();
    }
  }, [cargar]);

  useEffect(() => {
    setPendientes(pendientesEnCola());
    mandarPendientes();
    window.addEventListener('online', mandarPendientes);
    return () => window.removeEventListener('online', mandarPendientes);
  }, [mandarPendientes]);

  // Al elegir finca se propone su precio pactado. Queda editable: el día que
  // se pague distinto —por calidad, por un acuerdo— se corrige y el recibo
  // guarda el precio de ESE día, no el de la ficha.
  const elegirFinca = (id) => {
    setFincaId(id);
    const f = fincas.find(x => String(x.id) === String(id));
    if (!f) return;
    // Se propone la leche que esa finca vende. Si vende las dos, la de vaca,
    // que es la corriente; si solo vende búfala, esa. Proponer una que no
    // vende dejaría el precio en blanco sin explicar por qué.
    const vende = tiposQueVende(f);
    const t = vende.includes(tipoLeche) ? tipoLeche : (vende[0] || VACA);
    setTipoLeche(t);
    // La propia no tiene precio de compra que proponer.
    setPrecio(Number(f.propia) === 1 ? '' : String(Math.round(precioDe(f, t) || 0)));
    // Y LA RUTA SE LLENA SOLA con la de la ficha de la finca.
    //
    // Antes había que escogerla a mano en cada una de las setenta entregas del
    // día. Ahí es donde se equivoca quien anota, y una entrega en la ruta que
    // no es le mueve el rendimiento a dos rutas a la vez: a una le sobran
    // litros y a la otra le faltan.
    //
    // Queda editable a propósito: si un día esa leche llega a otra ruta, se
    // cambia y el recibo guarda a dónde llegó DE VERDAD, no a dónde suele.
    if (f.centro_id) setCentroId(String(f.centro_id));
  };

  // Cambiar de leche cambia el precio: son dos precios pactados distintos.
  const elegirLeche = (t) => {
    setTipoLeche(t);
    const f = fincas.find(x => String(x.id) === String(fincaId));
    if (!f) return;
    const p = precioDe(f, t);
    // Sin precio pactado se deja EN BLANCO, no se hereda el de la otra leche:
    // la búfala cuesta más, y arrastrar el precio de la vaca dejaría la entrega
    // anotada por menos de lo que se pagó sin que nada lo dijera.
    setPrecio(p ? String(Math.round(p)) : '');
  };

  const fincaElegida = fincas.find(x => String(x.id) === String(fincaId));
  const vendeEstas = fincaElegida ? tiposQueVende(fincaElegida) : [];
  // A la finca propia no se le paga: no se le pide precio ni se le calcula un
  // valor a pagar. Los litros se anotan igual — es la misma leche y se juzga
  // por rendimiento como cualquier otra.
  const esPropia = !!(fincaElegida && Number(fincaElegida.propia) === 1);
  const total = esPropia ? 0 : (valorDeEntrega({ litros, precio }) || 0);

  const guardar = async (e) => {
    e.preventDefault();
    setAviso(null);
    // El desplegable llevaba `required` y el navegador frenaba el envío solo.
    // La caja de búsqueda no es un campo de formulario, así que la comprobación
    // hay que hacerla aquí — si no, se guardaría un recibo sin finca y no
    // habría a quién pagarle esa leche.
    if (!fincaId) {
      return setAviso({ tipo: 'malo', texto: 'Falta escoger de cuál finca es la leche.' });
    }
    setGuardando(true);
    const f = fincas.find(x => String(x.id) === String(fincaId));
    const cuerpo = {
      finca_id: parseInt(fincaId),
      fecha: dia,
      litros: parseFloat(litros),
      precio_litro: esPropia ? 0 : parseFloat(precio),
      tipo_leche: tipoLeche,
      centro_id: centroId ? parseInt(centroId) : null,
      nota: nota || null,
    };

    // Sin señal NO se pierde lo tecleado: se guarda en el teléfono con su
    // llave y se manda solo cuando vuelva. El acopiador está en la finca, con
    // la leche ya recibida — decirle «no hay internet» y borrarle el dato es
    // exactamente lo que no puede pasar.
    if (!navigator.onLine) {
      const g = encolar(cuerpo);
      setGuardando(false);
      if (!g) {
        return setAviso({ tipo: 'malo',
          texto: 'No se pudo guardar en el teléfono. Anótelo en papel.' });
      }
      setPendientes(pendientesEnCola());
      setAviso({ tipo: 'ojo',
        texto: `Sin señal: ${peso(litros, 'L')} de ${f?.nombre} quedaron guardados ` +
               'en el teléfono. Se mandan solos cuando vuelva la señal.' });
      setLitros(''); setNota('');
      return;
    }

    try {
      const { data } = await axios.post('/api/recibos-leche', cuerpo);

      // Pactar el precio en la ficha, si se pidió. Va DESPUÉS de guardar el
      // recibo y en su propio try: si esto fallara, el recibo ya está a salvo
      // —que es lo que no se puede perder— y lo otro se vuelve a intentar.
      let pactado = false;
      // A LA FINCA PROPIA NO SE LE PACTA PRECIO: no se le paga, así que la
      // casilla va vacía a propósito. `parseFloat('')` da NaN, que al viajar se
      // vuelve `null`, y la columna no admite nulos: el guardado reventaba con
      // un error de base de datos.
      //
      // El recibo se salvaba —esto va en su propio try— pero quedaba un error
      // en el registro del servidor cada vez que se anotaba la leche de El
      // Algarrobo, y la ficha nunca se pactaba. Se encontró mirando el registro
      // de errores, no probando: en pantalla no se veía nada.
      const nuevoPrecio = parseFloat(precio);
      const sePuedePactar = pactar && f && !esPropia
        && Number.isFinite(nuevoPrecio) && nuevoPrecio > 0
        && !vendeEstas.includes(tipoLeche);
      if (sePuedePactar) {
        try {
          await axios.put(`/api/fincas/${f.id}`, {
            nombre: f.nombre,
            propietario: f.propietario || null,
            documento: f.documento || null,
            telefono: f.telefono || null,
            frecuencia_pago: f.frecuencia_pago,
            precio_litro: tipoLeche === 'bufala'
              ? (f.precio_litro || 0) : nuevoPrecio,
            precio_litro_bufala: tipoLeche === 'bufala'
              ? nuevoPrecio : (f.precio_litro_bufala || null),
          });
          pactado = true;
        } catch { /* el recibo quedó guardado, que es lo que importa */ }
      }

      setAviso({ tipo: 'bueno',
        texto: `Recibidos ${peso(litros, 'L')} de ${f?.nombre} por ${plata(data.total)}` +
               (pactado
                 ? `. El precio de ${tipoLeche === 'bufala' ? 'búfala' : 'vaca'} ` +
                   'quedó pactado en su ficha.'
                 : '') });
      setLitros('');
      setNota('');
      cargar();
    } catch (err) {
      // Si el servidor contestó, es un error de verdad —una finca borrada, un
      // dato malo— y hay que decirlo. Si NO contestó, fue la red: se guarda en
      // el teléfono igual que si no hubiera señal.
      if (err.response) {
        setAviso({ tipo: 'malo', texto: err.response.data?.error || 'No se pudo guardar' });
      } else {
        const g = encolar(cuerpo);
        setPendientes(pendientesEnCola());
        setAviso({ tipo: 'ojo',
          texto: g
            ? `Se cortó el envío: ${peso(litros, 'L')} de ${f?.nombre} quedaron ` +
              'guardados en el teléfono y se mandan solos.'
            : 'No se pudo guardar. Anótelo en papel.' });
        if (g) { setLitros(''); setNota(''); }
      }
    }
    setGuardando(false);
  };

  const corregir = async (e) => {
    e.preventDefault();
    const l = parseFloat(corrigiendo.litros);
    const pr = parseFloat(corrigiendo.precio_litro);
    if (!(l > 0) || !(pr > 0)) return alert('Litros y precio deben ser mayores que cero');
    try {
      // El total lo recalcula el servidor con los litros y el precio nuevos.
      // Mandarlo desde aquí sería tener dos versiones de la misma multiplicación.
      await axios.put(`/api/recibos-leche/${corrigiendo.id}`, {
        litros: l, precio_litro: pr,
        fecha: corrigiendo.fecha, nota: corrigiendo.nota || null,
        // El TIPO DE LECHE hay que mandarlo aunque no se toque. Sin él, el
        // servidor lo daba por vaca: corregirle los litros a una entrega de
        // búfala la convertía en vaca en silencio, y la búfala se paga más
        // caro y en factura aparte.
        tipo_leche: corrigiendo.tipo_leche || 'vaca',
        // Y la finca, que ahora se puede cambiar.
        finca_id: corrigiendo.finca_id ? parseInt(corrigiendo.finca_id) : undefined,
      });
      setCorrigiendo(null);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo corregir');
    }
  };

  const borrar = async (r) => {
    if (!window.confirm(
      `¿Borrar el recibo de ${peso(r.litros, 'L')} de ${r.finca} del ${fecha(r.fecha)}?\n\n` +
      'Cambiará lo que se le debe a esa finca.'
    )) return;
    try {
      await axios.delete(`/api/recibos-leche/${r.id}`);
      cargar();
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo borrar');
    }
  };

  // Lo recibido hoy, de todas las fincas. Es la cifra que se compara con lo que
  // se metió a las cuajadas.
  // EL CUADRE DEL DÍA: de lo que entró, cuánto queda.
  //
  // El cliente preguntó por qué los 2.784 L de arriba no bajaban al darle 300
  // a Juan Pablo. No deben bajar: ese número es lo que ENTRÓ comprado y es la
  // base de lo que se les paga a las fincas. Restarle la leche que sale dejaría
  // la relación de pagos por debajo de lo que los finqueros entregaron.
  //
  // Lo que faltaba era decir cuánta QUEDA. Y va en esta pantalla porque es
  // donde él mira la leche del día, no en la de vender.
  const cuadre = disponible(lecheDia || {});
  const suma = (k) => Math.round((cuadre.vaca[k] + cuadre.bufala[k]) * 10) / 10;
  const salioDeMas = cuadre.vaca.falta > 0 || cuadre.bufala.falta > 0;
  const hayBufala = cuadre.bufala.recibido > 0 || cuadre.bufala.cuajado > 0
                    || cuadre.bufala.vendido > 0;

  const esHoy = unSoloDia && desde === hoy();
  // Lo que llega del servidor ya viene acotado al periodo. Se vuelve a filtrar
  // igual por si una entrega recién anotada todavía no se ha recargado.
  const enRango = (f) => String(f).slice(0, 10) >= desde && String(f).slice(0, 10) <= hasta;
  const delDia = recibos.filter(r => enRango(r.fecha));
  // El tope del servidor. Si llegan exactamente 500 puede haber más y hay que
  // decirlo: un periodo largo que se recorta en silencio es el mismo fallo
  // otra vez, solo que más difícil de ver.
  const puedeHaberMas = recibos.length >= 5000;
  const litrosDelDia = delDia.reduce((s, r) => s + r.litros, 0);
  const plataDelDia = delDia.reduce((s, r) => s + r.total, 0);
  // Partido por leche cuando compra las dos: «2.784 L» sin decir cuánto de cada
  // una no sirve para cuadrar contra las tinas.
  const litrosVaca = delDia.filter(r => (r.tipo_leche || 'vaca') !== 'bufala')
                           .reduce((s, r) => s + r.litros, 0);
  const litrosBufala = litrosDelDia - litrosVaca;

  return (
    <>
      <div className="encabezado">
        <h1>Recibir leche</h1>
        <p>Lo que entra cada día, finca por finca</p>
      </div>

      {/* EL DÍA QUE SE ESTÁ MIRANDO.
          
          Va ARRIBA de las tarjetas y no dentro de una: gobierna las tres, y
          metido en una parecería que solo manda sobre esa.
          
          Cuando no es hoy se dice en ámbar y con un botón para volver. Una
          fecha vieja que se quedó puesta hace leer las cifras de otro día como
          si fueran las de hoy, y eso no se descubre hasta que algo no cuadra. */}
      <div className="bloque" style={{ marginBottom: 14 }}>
        <div className="cuerpo" style={{ display: 'flex', alignItems: 'flex-end',
                                         gap: 14, flexWrap: 'wrap' }}>
          {/* NINGUNA CASILLA BLOQUEA A LA OTRA.
              
              Estaban con `max={hasta}` y `min={desde}`, y eso las trababa: con
              hoy en las dos, ir primero a «Hasta» para poner el 16 encontraba
              todo lo anterior a hoy apagado en el calendario. No dejaba
              escogerlo, y al quedar vacía la casilla volvía sola a hoy — como
              si el programa ignorara lo que uno hace.
              
              Ahora se acomodan solas: si el hasta queda antes del desde, el
              otro lo sigue. Se corrige después en vez de impedir antes, que es
              lo que deja trabajar. */}
          <label className="campo" style={{ margin: 0, minWidth: 165 }}>
            <span>Desde</span>
            <input type="date" value={desde} max={hoy()}
                   onChange={e => {
                     const v = e.target.value || hoy();
                     setDesde(v);
                     if (v > hasta) setHasta(v);
                   }} />
          </label>
          <label className="campo" style={{ margin: 0, minWidth: 165 }}>
            <span>Hasta</span>
            <input type="date" value={hasta} max={hoy()}
                   onChange={e => {
                     const v = e.target.value || hoy();
                     setHasta(v);
                     if (v < desde) setDesde(v);
                   }} />
          </label>
          {/* Un solo día se pide poniendo la misma fecha dos veces, y eso
              nadie lo adivina. El atajo lo hace por él. */}
          {!unSoloDia && (
            <button className="boton chico suave" style={{ marginBottom: 8 }}
                    onClick={() => setDesde(hasta)}>
              Solo el {fecha(hasta)}
            </button>
          )}
          {esHoy ? (
            <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', paddingBottom: 10 }}>
              Cámbielas para ver otro día, o un periodo entero.
            </div>
          ) : (
            <div className="aviso ojo" style={{ margin: 0, flex: '1 1 230px' }}>
              {unSoloDia
                ? <>Está viendo el <strong>{fecha(desde)}</strong>, no hoy.</>
                : <>Está viendo <strong>del {fecha(desde)} al {fecha(hasta)}</strong>.</>}
              <button className="boton chico suave" style={{ marginLeft: 10 }}
                      onClick={() => { setDesde(hoy()); setHasta(hoy()); }}>
                Volver a hoy
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="rejilla">
        <div className="dato" style={{ '--color': 'var(--leche)' }}>
          <div className="rotulo">
            🥛 {esHoy ? 'Recibido hoy'
                 : unSoloDia ? `Recibido el ${fecha(desde)}`
                 : `Recibido del ${fecha(desde)} al ${fecha(hasta)}`}
          </div>
          <div className="valor cifra">{peso(litrosDelDia, 'L')}</div>
          <div className="pie">
            {plata(plataDelDia)} · {delDia.length} {delDia.length === 1 ? 'entrega' : 'entregas'}
          </div>
          {/* Partido por leche solo cuando compra las dos. Al que solo compra
              de vaca este renglón no le dice nada y sobra. */}
          {litrosBufala > 0 && (
            <div className="pie" style={{ marginTop: 2 }}>
              🐄 {peso(litrosVaca, 'L')} · 🐃 {peso(litrosBufala, 'L')}
            </div>
          )}
          {/* Ya no hace falta el botón de «ver cuáles fincas»: la lista de
              abajo sigue este mismo rango, así que las fincas ya están ahí. */}
        </div>
        {/* Siempre a la vista, aunque no haya salido nada.
            
            Escondiéndola cuando no hay leche vendida, la única vez que alguien
            va a buscarla —el día que las cuentas no cuadran— no está. Y un
            descuadre hay que verlo el MISMO día: al cierre del mes ya no hay
            a quién preguntarle. */}
        <div className="dato" style={{ '--color': salioDeMas ? 'var(--malo)' : 'var(--leche)' }}>
          <div className="rotulo">
            {salioDeMas ? '⚠️ No cuadra' : '🥛 Le queda sin cuajar'}
            {!unSoloDia && <> · {fecha(hasta)}</>}
          </div>
          <div className="valor cifra" style={salioDeMas ? { color: 'var(--malo)' } : undefined}>
            {salioDeMas
              ? peso(Math.round((cuadre.vaca.falta + cuadre.bufala.falta) * 10) / 10, 'L')
              : peso(suma('queda'), 'L')}
          </div>
          <div className="pie">
            {salioDeMas
              ? 'salieron más litros de los que entraron — revise las entregas'
              : (suma('cuajado') === 0 && suma('vendido') === 0
                  ? 'todavía no se ha cuajado ni vendido nada'
                  : `${peso(suma('cuajado'), 'L')} a las tinas` +
                    (suma('vendido') > 0 ? ` · ${peso(suma('vendido'), 'L')} vendidos crudos` : ''))}
          </div>
          {hayBufala && !salioDeMas && (
            <div className="pie" style={{ marginTop: 2 }}>
              🐄 {peso(cuadre.vaca.queda, 'L')} · 🐃 {peso(cuadre.bufala.queda, 'L')}
            </div>
          )}
        </div>
        <div className="dato" style={{ '--color': 'var(--leche)' }}>
          <div className="rotulo">
            💧 Precio promedio {esHoy ? 'de hoy' : unSoloDia ? 'de ese día' : 'del periodo'}
          </div>
          <div className="valor cifra">
            {litrosDelDia > 0 ? plata(plataDelDia / litrosDelDia) + '/L' : '—'}
          </div>
          <div className="pie">
            {litrosDelDia > 0
              ? (esHoy ? 'es el que costea las cuajadas de hoy'
                 : unSoloDia ? 'el que costeó las cuajadas de ese día'
                 : 'promedio ponderado de todo el periodo')
              : (unSoloDia ? 'sin entregas ese día' : 'sin entregas en ese periodo')}
          </div>
        </div>
      </div>

      {/* El aviso de lo que falta por mandar.
          Va arriba de todo y solo aparece cuando hay algo: un cartel
          permanente se vuelve parte del fondo. Sin este renglón, un recibo
          podría quedarse semanas en el teléfono sin que nadie lo supiera. */}
      {pendientes.length > 0 && (
        <div className="aviso ojo">
          <strong>
            ⏳ {pendientes.length}{' '}
            {pendientes.length === 1 ? 'recibo guardado' : 'recibos guardados'} en
            este teléfono, sin enviar.
          </strong>
          <div style={{ fontSize: 14, marginTop: 4 }}>
            {pendientes.map(r => {
              const f = fincas.find(x => x.id === r.finca_id);
              return `${peso(r.litros, 'L')} de ${f ? f.nombre : 'una finca'}`;
            }).join(' · ')}
          </div>
          <div style={{ marginTop: 8 }}>
            <button className="boton chico" disabled={mandando} onClick={mandarPendientes}>
              {mandando ? 'Enviando…' : navigator.onLine ? 'Enviar ahora' : 'Sin señal todavía'}
            </button>
          </div>
        </div>
      )}

      <div className="bloque">
        <h2>🥛 Anotar una entrega</h2>
        <form className="cuerpo" onSubmit={guardar}>
          {aviso && <div className={'aviso ' + aviso.tipo}>{aviso.texto}</div>}

          {fincas.length === 0 ? (
            <div className="aviso ojo">
              Todavía no hay fincas registradas. Agrega una en la pantalla de
              Fincas antes de recibir leche.
            </div>
          ) : (
            <>
              <div className="campos">
                <label className="campo">
                  <span>Finca *</span>
                  {/* Caja de búsqueda en vez de desplegable. Con cincuenta
                      fincas, bajar por una lista alfabética en un teléfono
                      parado en la finca es lo que hace lenta esta pantalla —y
                      es la que más se usa del programa. */}
                  <ElegirFinca fincas={fincas} valor={fincaId} alElegir={elegirFinca} />
                </label>
              {/* Con un solo centro no se pregunta: se anota solo. Con varios
                  hay que elegir, porque de ahí sale el rendimiento de cada
                  sitio y la leche que llegó a cada uno. */}
              {centros.length > 1 && (
                <label className="campo">
                  <span>¿A cuál ruta llegó? *</span>
                  <select value={centroId} required
                          onChange={e => setCentroId(e.target.value)}>
                    <option value="">— Elegir —</option>
                    {centros.map(c => (
                      <option key={c.id} value={c.id}>{c.nombre}</option>
                    ))}
                  </select>
                  {/* Se DICE que vino de la ficha, en vez de aparecer llena
                      como por arte de magia. Quien no sabe de dónde salió un
                      dato no sabe cuándo desconfiar de él. */}
                  {fincaElegida && fincaElegida.centro_id
                    && String(fincaElegida.centro_id) === String(centroId) && (
                    <small style={{ color: 'var(--tinta-tenue)' }}>
                      La ruta de {fincaElegida.nombre}. Cámbiela si hoy llegó a otra.
                    </small>
                  )}
                </label>
              )}


                {/* De cuál leche. Solo se ofrecen las que ESA finca vende
                    —las que tienen precio pactado—, para que no se pueda
                    anotar una entrega de búfala a quien solo tiene vaca por un
                    dedazo. Con una sola leche no se pregunta nada. */}
                {/* La pregunta va SIEMPRE, aunque esa finca solo tenga
                    pactada una leche.
                    Antes solo salía si la finca tenía los dos precios, y eso
                    obligaba a ir a editar la ficha ANTES de poder anotar la
                    primera entrega de búfala. Además, quien recibe piensa «¿de
                    cuál es esta?», no «¿qué tiene configurado el programa?»:
                    esconder la pregunta hace parecer que el programa decidió.

                    Lo que se protege no es la pregunta, es el PRECIO — abajo. */}
                <label className="campo">
                  <span>¿Cuál leche? *</span>
                  <select value={tipoLeche} required
                          onChange={e => elegirLeche(e.target.value)}>
                    {TIPOS.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.ico} {t.nombre}
                        {/* La propia no tiene precio pactado A PROPÓSITO: no
                            se le paga. Decírselo la hace ver incompleta y
                            manda a arreglar algo que está bien. */}
                        {fincaElegida && !esPropia && !vendeEstas.includes(t.id)
                          ? ' — sin precio pactado' : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="campo">
                  <span>Litros *</span>
                  <input type="number" step="0.1" min="0" className="peso" required
                         value={litros} onChange={e => setLitros(e.target.value)}
                         placeholder="0" />
                </label>

                {/* A la finca propia no se le pide precio. Dejar la casilla
                    obligatoria la dejaba fuera del programa: no había forma de
                    anotar esa leche. Y se DICE por qué está apagada, en vez de
                    esconderla: una casilla que desaparece sin explicación se
                    lee como que algo se dañó. */}
                {esPropia ? (
                  <label className="campo">
                    <span>Precio por litro</span>
                    <div style={{ padding: '9px 0', color: 'var(--tinta-tenue)',
                                  fontSize: 14 }}>
                      Finca propia — no se paga
                    </div>
                  </label>
                ) : (
                  <label className="campo">
                    <span>Precio por litro *</span>
                    <input type="number" step="1" min="0" className="peso" required
                           value={precio} onChange={e => setPrecio(e.target.value)} />
                  </label>
                )}

                <label className="campo">
                  <span>Fecha</span>
                  <input type="date" value={dia} onChange={e => setDia(e.target.value)} />
                </label>

                <label className="campo">
                  <span>Nota</span>
                  <input value={nota} onChange={e => setNota(e.target.value)}
                         placeholder="Opcional" />
                </label>
              </div>

              {/* Sin precio pactado NO se bloquea: se pide aquí y se ofrece
                  guardarlo. Bloquear obligaría a salirse a media entrega, con
                  el finquero esperando al lado de la cantina. */}
              {/* Y NUNCA a la finca propia: a esa no se le paga, así que
                  pedirle un precio pactado es pedirle algo que por diseño no
                  tiene. El aviso salía igual y contradecía a la casilla de
                  arriba, que dos renglones antes dice «no se paga». */}
              {fincaElegida && !esPropia && !vendeEstas.includes(tipoLeche) && (
                <div className="aviso ojo" style={{ marginTop: 12 }}>
                  <strong>
                    {fincaElegida.nombre} no tiene precio pactado de{' '}
                    {tipoLeche === 'bufala' ? 'búfala' : 'vaca'}.
                  </strong>{' '}
                  Escriba arriba a cuánto le está pagando esta entrega.
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center',
                                  marginTop: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={pactar}
                           onChange={e => setPactar(e.target.checked)} />
                    <span>
                      Guardarlo en la ficha de la finca, para no volver a
                      escribirlo
                    </span>
                  </label>
                </div>
              )}

              {/* LO QUE VALE ESTA ENTREGA, en grande.
                  Estaba, pero en un renglón chico debajo del formulario. Es el
                  número que el finquero pregunta ahí mismo, con la cantina en
                  la mano — no al final de la quincena—, y quien recibe tiene
                  que poder leérselo sin buscarlo. */}
              {total > 0 && (
                <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 10,
                              background: 'var(--hondo)',
                              borderLeft: '4px solid var(--acento-vivo)' }}>
                  <div style={{ fontSize: 12, color: 'var(--tinta-tenue)' }}>
                    Esta entrega vale
                  </div>
                  <div className="cifra recio" style={{ fontSize: 30, lineHeight: 1.1 }}>
                    {plata(total)}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 4 }}>
                    {peso(litros, 'L')} × {plata(precio)}
                    {vendeEstas.length > 1 &&
                      ' · ' + (TIPOS.find(t => t.id === tipoLeche) || TIPOS[0]).nombre}
                    {' '}· se le quedan debiendo a esa finca
                  </div>
                </div>
              )}

              <div className="acciones">
                <button className="boton" disabled={guardando}>
                  {guardando ? 'Guardando…' : '💾 Anotar la entrega'}
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      {corrigiendo && (
        <div className="bloque">
          <h2>✏️ Corregir el recibo de {corrigiendo.finca}</h2>
          <form className="cuerpo" onSubmit={corregir}>
            <div className="aviso">
              El total se vuelve a calcular solo con los litros y el precio que
              deje aquí. Lo que ya se le haya pagado a la finca no se toca: el
              saldo se recalcula.
            </div>
            <div className="campos">
              {/* CAMBIAR DE FINCA. «Se equivocó con un cliente»: anotó la
                  entrega a quien no era. Sin esto había que borrarla y
                  volverla a escribir, y el que borra por equivocación pierde
                  el registro entero. */}
              <label className="campo">
                <span>Finca</span>
                <select value={corrigiendo.finca_id || ''}
                        onChange={e => setCorrigiendo({ ...corrigiendo, finca_id: e.target.value })}>
                  {fincas.map(f => (
                    <option key={f.id} value={f.id}>{f.nombre}</option>
                  ))}
                </select>
              </label>
              <label className="campo">
                <span>¿Cuál leche?</span>
                <select value={corrigiendo.tipo_leche || 'vaca'}
                        onChange={e => setCorrigiendo({ ...corrigiendo, tipo_leche: e.target.value })}>
                  <option value="vaca">🐄 Vaca</option>
                  <option value="bufala">🐃 Búfala</option>
                </select>
              </label>
              <label className="campo">
                <span>Litros *</span>
                <input type="number" step="0.1" min="0" className="peso" required autoFocus
                       value={corrigiendo.litros}
                       onChange={e => setCorrigiendo({ ...corrigiendo, litros: e.target.value })} />
              </label>
              <label className="campo">
                <span>Precio por litro *</span>
                <input type="number" step="1" min="0" className="peso" required
                       value={corrigiendo.precio_litro}
                       onChange={e => setCorrigiendo({ ...corrigiendo, precio_litro: e.target.value })} />
              </label>
              <label className="campo">
                <span>Fecha</span>
                <input type="date" value={corrigiendo.fecha}
                       onChange={e => setCorrigiendo({ ...corrigiendo, fecha: e.target.value })} />
              </label>
              <label className="campo">
                <span>Nota</span>
                <input value={corrigiendo.nota} placeholder="Opcional"
                       onChange={e => setCorrigiendo({ ...corrigiendo, nota: e.target.value })} />
              </label>
            </div>
            <div className="aviso" style={{ marginBottom: 0 }}>
              Queda en{' '}
              <strong className="cifra">
                {plata((parseFloat(corrigiendo.litros) || 0) * (parseFloat(corrigiendo.precio_litro) || 0))}
              </strong>
            </div>
            <div className="acciones">
              <button type="button" className="boton suave" onClick={() => setCorrigiendo(null)}>
                Cancelar
              </button>
              <button className="boton">💾 Guardar la corrección</button>
            </div>
          </form>
        </div>
      )}

      <div className="bloque" id="entregas">
        <h2>
          📋 {esHoy ? 'Últimas entregas'
               : unSoloDia ? `Entregas del ${fecha(desde)}`
               : `Entregas del ${fecha(desde)} al ${fecha(hasta)}`}
          <span className="contador">
            {delDia.length}{delDia.length !== recibos.length && ` de ${recibos.length}`}
          </span>
        </h2>
        {recibos.length === 0 ? (
          <div className="vacio">Todavía no se ha anotado ninguna entrega.</div>
        ) : (() => {
          // ── LA CAJA DE BÚSQUEDA ──
          //
          // Quien manda la información se equivoca y hay que corregir. Con
          // doscientas entregas, encontrar la de ayer de SANTA ELENA bajando
          // con el dedo no se puede.
          //
          // Y hasta hoy ni siquiera se podía bajando: la tabla enseñaba las
          // primeras 60 y las demás no existían para quien mirara la pantalla.
          // Ese tope estaba puesto para que la tabla no se volviera eterna, y
          // el precio era que tres de cada cuatro entregas quedaran invisibles.
          // Primero el rango de arriba, después lo que se escriba aquí. Así
          // «eufracio» del 16 al 20 da las de él en esos cinco días, que es la
          // pregunta que se hace de verdad — no todas las suyas de siempre.
          const hallados = filtrarRecibos(recibos.filter(r => enRango(r.fecha)),
                                          buscando, fecha);
          const CABEN = verTodas ? hallados.length : 60;
          const enPantalla = hallados.slice(0, CABEN);
          return (
          <>
            <div style={{ marginBottom: 12 }}>
              <input value={buscando} onChange={e => setBuscando(e.target.value)}
                     placeholder="🔍 Buscar por finca, fecha o cantidad — «SANTA ELENA», «22 de ago», «220»" />
              {buscando.trim() !== '' && (
                <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 5 }}>
                  {/* Contra las del RANGO, no contra todas: buscando dentro
                      del 16 al 20, «5 de 60» hace pensar que se están viendo
                      cinco de sesenta cuando en esos días solo hay 25. */}
                  {hallados.length === 0
                    ? 'Ninguna entrega coincide.'
                    : `${hallados.length} de ${delDia.length} en este periodo`}
                </div>
              )}
            </div>
          {hallados.length === 0 ? (
            <div className="vacio">
              {buscando.trim() === ''
                ? <>No se anotó ninguna entrega {unSoloDia
                    ? <>el <strong>{fecha(desde)}</strong></>
                    : <>entre el <strong>{fecha(desde)}</strong> y el <strong>{fecha(hasta)}</strong></>}.
                    {/* Una salida, en vez de dejarlo mirando una lista vacía.
                        Abrir la pantalla temprano —antes de que llegue la
                        leche— da cero, y sin este botón parece que se
                        perdieron las de ayer. */}
                    <div style={{ marginTop: 10 }}>
                      <button className="boton chico suave"
                              onClick={() => {
                                const d = new Date(hasta + 'T00:00:00');
                                d.setDate(d.getDate() - 7);
                                setDesde(d.toISOString().slice(0, 10));
                              }}>
                        Ver los últimos 7 días
                      </button>
                    </div></>
                : <>Ninguna entrega con «{buscando}» en ese periodo. Pruebe con
                    el nombre de la finca, o amplíe las fechas de arriba.</>}
            </div>
          ) : (
          <div className="tabla-caja">
            <table className="datos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Finca</th>
                  <th style={{ textAlign: 'right' }}>Litros</th>
                  <th style={{ textAlign: 'right' }}>Precio</th>
                  <th style={{ textAlign: 'right' }}>Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {enPantalla.map(r => (
                  <tr key={r.id}>
                    <td>{fecha(r.fecha)}</td>
                    <td className="recio">{r.finca}</td>
                    <td className="num">{peso(r.litros, 'L')}</td>
                    <td className="num">{plata(r.precio_litro)}</td>
                    <td className="num recio">{plata(r.total)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="boton chico suave" style={{ marginRight: 4 }}
                              onClick={() => setCorrigiendo({
                                id: r.id, finca: r.finca,
                                // La finca y la leche viajan aunque no se
                                // toquen: sin ellas el guardado las perdía.
                                finca_id: String(r.finca_id),
                                tipo_leche: r.tipo_leche || 'vaca',
                                litros: String(r.litros),
                                precio_litro: String(Math.round(r.precio_litro)),
                                fecha: String(r.fecha).slice(0, 10),
                                nota: r.nota || '',
                              })}
                              title="Corregir">
                        ✏️
                      </button>
                      <button className="boton chico suave" onClick={() => borrar(r)} title="Borrar">
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
          {/* CUÁNTAS NO CABEN. Antes se cortaba en 60 sin decirlo, y quien
              buscaba una vieja concluía que no se había anotado. */}
          {/* EL TOPE DEL SERVIDOR, dicho. Un periodo largo se recorta en 500
              y sin este renglón vuelve a pasar lo del 16 de agosto: faltan
              entregas y nada lo delata. */}
          {puedeHaberMas && (
            <div className="aviso ojo" style={{ marginTop: 10 }}>
              Ese periodo tiene <strong>más de 5.000 entregas</strong> y el
              programa trae solo las 5.000 más recientes. Acorte las fechas de
              arriba para verlas todas.
            </div>
          )}
          {hallados.length > CABEN && (
            <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 10,
                          display: 'flex', alignItems: 'center', gap: 10,
                          flexWrap: 'wrap' }}>
              <span>Se ven las {CABEN} más recientes de {hallados.length}.</span>
              <button className="boton chico suave" onClick={() => setVerTodas(true)}>
                Ver las {hallados.length - CABEN} restantes
              </button>
            </div>
          )}
          {/* Y el camino de vuelta. Sin él, quien soltó las quinientas se queda
              con la pantalla pesada para siempre. */}
          {verTodas && hallados.length > 60 && (
            <div style={{ fontSize: 13, color: 'var(--tinta-tenue)', marginTop: 10 }}>
              Se ven las {hallados.length}.{' '}
              <button className="boton chico suave" onClick={() => setVerTodas(false)}>
                Ver solo las 60 últimas
              </button>
            </div>
          )}
          </>
          );
        })()}
      </div>
    </>
  );
}

export default RecibirLeche;
